/**
 * useUnsavedChangesGuard
 *
 * Warns the user before they leave a page that has unsaved work -- form edits
 * still in progress, or a video/media upload that's still running. Losing
 * either to an accidental back-tap or tab close is a top frustration.
 *
 * Covers both navigation paths:
 *   - In-app React Router navigation -> `useBlocker` surfaces a confirm modal.
 *   - Browser tab close / refresh / `window.location` -> `beforeunload` shows
 *     the native "Leave site?" prompt.
 *
 * After a successful save the page navigates programmatically and must NOT be
 * blocked. Call `allowNavigation()` synchronously right before that navigate
 * (or `window.location` assignment); it bypasses both guards for what follows.
 *
 * `when` is read through a ref so the predicate and the beforeunload handler
 * always see the latest value without re-subscribing.
 */

import { useEffect, useRef } from "react"
import { useBlocker } from "react-router-dom"

export interface UnsavedChangesGuard {
  /** True while an in-app navigation is held pending the user's choice. */
  isBlocked: boolean
  /** Proceed with the blocked navigation (user chose "Leave"). */
  confirmLeave: () => void
  /** Cancel the blocked navigation (user chose "Keep editing"). */
  cancelLeave: () => void
  /** Bypass the guard for the next navigation (call before a post-save navigate). */
  allowNavigation: () => void
}

export function useUnsavedChangesGuard(when: boolean): UnsavedChangesGuard {
  const whenRef = useRef(when)
  whenRef.current = when
  const bypassRef = useRef(false)

  // Deliberately compares pathname only: same-route query/hash changes (e.g.
  // opening a ?share modal) shouldn't trigger the leave prompt. Tab close /
  // full-page navigations are still covered by the beforeunload handler below.
  const blocker = useBlocker(({ currentLocation, nextLocation }) => {
    if (currentLocation.pathname === nextLocation.pathname) return false
    // A post-save bypass is one-shot: it lets the next navigation through (which
    // may be deferred, e.g. a success modal the user clicks later) and re-arms,
    // so a page that survives the save still guards subsequent edits.
    if (bypassRef.current) {
      bypassRef.current = false
      return false
    }
    return whenRef.current
  })

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      // bypassRef also covers a deferred full-page post-save navigation
      // (window.location); the page unloads, so it needn't be reset here.
      if (whenRef.current && !bypassRef.current) {
        event.preventDefault()
        // Legacy browsers require returnValue to be set to trigger the prompt.
        event.returnValue = ""
      }
    }
    window.addEventListener("beforeunload", handleBeforeUnload)
    return () => window.removeEventListener("beforeunload", handleBeforeUnload)
  }, [])

  return {
    isBlocked: blocker.state === "blocked",
    confirmLeave: () => blocker.proceed?.(),
    cancelLeave: () => blocker.reset?.(),
    // Allow the next navigation past the guard (call before a post-save
    // navigate, or once at save time for a deferred success-modal navigation).
    // The bypass is consumed by that navigation, so the guard re-arms after.
    allowNavigation: () => {
      bypassRef.current = true
    },
  }
}
